import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  ArrowUpRight, Check, ChevronDown, FileArchive, FolderGit2, Github, Gitlab, Lightbulb, Link2, ListChecks, Loader2, Plus, Search,
} from 'lucide-react';
import { Action, PageBody, PageHead, Panel } from '../../components/aa';
import { aaiApi } from '../../services/aaiApi';
import { useOpenWorkspace } from '../../hooks/useOpenWorkspace';
import { AddClientModal } from '../provider/ProviderClientsPage';
import { STATUS_LABEL } from '../partner/format';
import type { CodeHostState, CodeHostStatus, PartnerClientRow } from '../../types/aai';

type DoorKey = 'code' | 'build' | 'problem';
type KindKey = 'custom' | 'skill' | 'plugin';

interface Target {
  id: string;
  name: string;
  initials: string;
  own: boolean;
}

const DOORS: Array<{ key: DoorKey; title: string; body: string; path: string[]; icon: React.ReactNode }> = [
  {
    key: 'code',
    title: 'I have the code',
    body: 'GitHub, GitLab or a zip. We read it and understand it first.',
    path: ['Read the code', 'Questions', 'PRD', 'Project'],
    icon: <FolderGit2 size={20} />,
  },
  {
    key: 'build',
    title: 'I know what to build',
    body: 'Pick the kind and answer a few questions. A skill or plugin gets built; a custom build gets a PRD.',
    path: ['Questions', 'Built, or a PRD'],
    icon: <ListChecks size={20} />,
  },
  {
    key: 'problem',
    title: 'I have a problem',
    body: 'Describe it and see the ways to solve it. Then pick one.',
    path: ['Questions', 'Ways to solve it', 'Pick one'],
    icon: <Lightbulb size={20} />,
  },
];

const KINDS: Array<{ key: KindKey; title: string; body: string }> = [
  { key: 'custom', title: 'Custom build', body: 'Software made for you. Orbit writes a PRD to accept before anyone builds.' },
  { key: 'skill', title: 'Skill', body: 'Teach Orbit one of your procedures so it does it the same way every time.' },
  { key: 'plugin', title: 'Plugin', body: 'A set of skills and tools your team installs and shares.' },
];

const HOSTS: Array<{ key: 'github' | 'gitlab'; title: string; icon: React.ReactNode }> = [
  { key: 'github', title: 'GitHub', icon: <Github size={16} /> },
  { key: 'gitlab', title: 'GitLab', icon: <Gitlab size={16} /> },
];

const RECENT_COUNT = 3;

const initialsOf = (name: string): string => {
  const letters = name.trim().split(/\s+/).map((w) => w.charAt(0)).join('');
  return letters.slice(0, 2).toUpperCase() || '?';
};

const clientLine = (c: PartnerClientRow): string => {
  if (c.projects_open === 1) return '1 project open';
  if (c.projects_open > 1) return `${c.projects_open} projects open`;
  return STATUS_LABEL[c.status] || '';
};

const activityAt = (c: PartnerClientRow): number => {
  const at = c.last_activity?.at;
  if (!at) return 0;
  return new Date(at).getTime() || 0;
};

const destinationFor = (workspaceId: string, door: DoorKey, kind: KindKey): string => {
  const qs = new URLSearchParams({ workspaceId, start: door });
  if (door === 'build') qs.set('kind', kind);
  return `/customer/workspace?${qs.toString()}`;
};

const Mark: React.FC<{ text: string; own?: boolean }> = ({ text, own = false }) => (
  <span
    className={clsx(
      'grid h-8 w-8 shrink-0 place-items-center rounded-[9px] text-[11px] font-semibold',
      own && 'bg-accent-500 text-black',
      !own && 'border border-line bg-white text-ink',
    )}
  >
    {text}
  </span>
);

const PickerRow: React.FC<{ title: string; line: string; mark: React.ReactNode; on: boolean; onPick: () => void }> = ({
  title, line, mark, on, onPick,
}) => (
  <button
    type="button"
    onClick={onPick}
    className={clsx(
      'flex w-full items-center gap-3 rounded-act px-2.5 py-2 text-left transition-colors hover:bg-canvas-inset',
      on && 'bg-canvas-inset',
    )}
  >
    {mark}
    <span className="min-w-0 flex-1">
      <span className="block truncate text-[13px] font-semibold text-ink">{title}</span>
      {line && <span className="block truncate text-[12px] text-ink-muted">{line}</span>}
    </span>
    {on && <Check size={15} className="shrink-0 text-ink" />}
  </button>
);

const GroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="m-0 px-2.5 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-soft">{children}</p>
);

const ForPicker: React.FC<{
  own: Target;
  clients: PartnerClientRow[];
  value: Target;
  onChange: (t: Target) => void;
  onAddClient: () => void;
}> = ({ own, clients, value, onChange, onAddClient }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pick = (t: Target) => {
    onChange(t);
    setOpen(false);
    setQ('');
  };
  const pickClient = (c: PartnerClientRow) => pick({ id: c.id, name: c.name, initials: c.initials || initialsOf(c.name), own: false });

  const needle = q.trim().toLowerCase();
  const recent = clients.filter((c) => activityAt(c) > 0).sort((a, b) => activityAt(b) - activityAt(a)).slice(0, RECENT_COUNT);
  const recentIds = new Set(recent.map((c) => c.id));
  const rest = clients.filter((c) => !recentIds.has(c.id)).sort((a, b) => a.name.localeCompare(b.name));
  const matches = clients.filter((c) => (c.name || '').toLowerCase().includes(needle) || (c.contact_email || '').toLowerCase().includes(needle));
  const ownMatches = !needle || own.name.toLowerCase().includes(needle);

  const clientRow = (c: PartnerClientRow) => (
    <PickerRow
      key={c.id}
      title={c.name}
      line={clientLine(c)}
      mark={<Mark text={c.initials || initialsOf(c.name)} />}
      on={value.id === c.id}
      onPick={() => pickClient(c)}
    />
  );

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-11 items-center gap-2.5 rounded-act border border-line bg-white pl-3.5 pr-3 text-[13px] transition-colors hover:border-line-strong"
      >
        <span className="text-ink-muted">For</span>
        <Mark text={value.initials} own={value.own} />
        <span className="max-w-[180px] truncate font-semibold text-ink">{value.name}</span>
        <ChevronDown size={15} className="shrink-0 text-ink-soft" />
      </button>
      {open && (
        <div className="absolute right-0 top-full z-[60] mt-2 w-[320px] max-w-[calc(100vw-2rem)] rounded-panel border border-line bg-white p-2 shadow-[0_12px_32px_rgba(24,25,29,0.14)]">
          <label className="flex h-10 items-center gap-2 rounded-act bg-canvas-inset px-3">
            <Search size={14} className="shrink-0 text-ink-soft" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search clients"
              aria-label="Search clients"
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-ink outline-none placeholder:text-ink-soft focus:ring-0"
            />
          </label>
          <div className="max-h-[360px] overflow-y-auto">
            {ownMatches && (
              <>
                <GroupLabel>You</GroupLabel>
                <PickerRow title={own.name} line="Your own work" mark={<Mark text={own.initials} own />} on={value.own} onPick={() => pick(own)} />
              </>
            )}
            {needle && matches.length > 0 && (
              <>
                <GroupLabel>Clients</GroupLabel>
                {matches.map(clientRow)}
              </>
            )}
            {!needle && recent.length > 0 && (
              <>
                <GroupLabel>Recent</GroupLabel>
                {recent.map(clientRow)}
              </>
            )}
            {!needle && rest.length > 0 && (
              <>
                <GroupLabel>All clients</GroupLabel>
                {rest.map(clientRow)}
              </>
            )}
            {needle && matches.length === 0 && !ownMatches && (
              <p className="m-0 px-2.5 py-3 text-[13px] text-ink-muted">No client matches "{q}".</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => { setOpen(false); onAddClient(); }}
            className="mt-1 flex w-full items-center gap-2 border-t border-line-soft px-2.5 pb-1 pt-2.5 text-left text-[13px] font-semibold text-ink hover:text-ink-muted"
          >
            <Plus size={14} />
            Add a client
          </button>
        </div>
      )}
    </div>
  );
};

const DoorCard: React.FC<{ door: typeof DOORS[number]; on: boolean; onPick: () => void }> = ({ door, on, onPick }) => (
  <button
    type="button"
    onClick={onPick}
    aria-pressed={on}
    className={clsx(
      'flex h-full flex-col rounded-panel border bg-white p-5 text-left transition-colors',
      on && 'border-ink shadow-[inset_0_0_0_1px_#18191D]',
      !on && 'border-line hover:border-line-strong',
    )}
  >
    <span
      className={clsx(
        'grid h-11 w-11 place-items-center rounded-tile',
        on && 'bg-accent-500 text-black',
        !on && 'bg-canvas-inset text-ink-muted',
      )}
    >
      {door.icon}
    </span>
    <span className="mt-4 block text-[17px] font-bold leading-[24px] text-ink">{door.title}</span>
    <span className="mt-1.5 block flex-1 text-[13px] leading-[20px] text-ink-muted">{door.body}</span>
    <span className="mt-4 flex flex-wrap items-center gap-x-1.5 gap-y-1 border-t border-line-soft pt-3 text-[12px] text-ink-soft">
      {door.path.map((step, i) => (
        <React.Fragment key={step}>
          {i > 0 && <span aria-hidden>›</span>}
          <span className={clsx(i === door.path.length - 1 && 'font-semibold text-ink')}>{step}</span>
        </React.Fragment>
      ))}
    </span>
  </button>
);

const SourceTitle: React.FC<{ icon: React.ReactNode; title: string; children?: React.ReactNode }> = ({ icon, title, children }) => (
  <span className="flex items-center gap-2 text-[14px] font-semibold text-ink">
    <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-canvas-inset text-ink">{icon}</span>
    <span className="flex-1">{title}</span>
    {children}
  </span>
);

const HostBadge: React.FC<{ state: CodeHostState | null; loading: boolean }> = ({ state, loading }) => {
  let label = 'Not connected';
  let dot = 'bg-line-strong';
  if (state?.connected) {
    label = 'Connected';
    dot = 'bg-ok-dim';
  }
  if (state && !state.known) label = 'Status unavailable';
  if (loading) label = 'Checking';
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-muted">
      {!loading && <span aria-hidden className={clsx('h-2 w-2 rounded-full', dot)} />}
      {loading && <Loader2 size={12} className="animate-spin" />}
      {label}
    </span>
  );
};

const ownerLine = (state: CodeHostState | null, title: string): string => {
  if (state?.connected && state.account) return `As ${state.account}. Pick a repository, or paste its link.`;
  if (state?.connected) return 'Pick a repository, or paste its link.';
  return `Connect ${title} to pick from your repositories, or paste a public link.`;
};

const memberLine = (state: CodeHostState | null): string => {
  if (state?.account) return `As ${state.account}. Pick one of the company's repositories.`;
  return "Pick one of the company's repositories.";
};

const SourceTile: React.FC<{
  icon: React.ReactNode;
  title: string;
  badge?: React.ReactNode;
  line: string;
  action: string;
  disabled: boolean;
  onClick: () => void;
}> = ({ icon, title, badge, line, action, disabled, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="group flex flex-col rounded-act border border-line p-4 text-left transition-colors hover:border-line-strong disabled:cursor-wait"
  >
    <SourceTitle icon={icon} title={title}>{badge}</SourceTitle>
    <span className="mt-2 block flex-1 text-[12.5px] leading-[19px] text-ink-muted">{line}</span>
    <span className="mt-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-ink group-hover:underline">
      {action}
      <ArrowUpRight size={13} />
    </span>
  </button>
);

const GRID_COLS: Record<number, string> = { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3' };

const CodeDetail: React.FC<{
  hosts: CodeHostStatus | null;
  loading: boolean;
  opening: boolean;
  onOpenCode: () => void;
  onPickRepo: () => void;
  onUpload: () => void;
}> = ({ hosts, loading, opening, onOpenCode, onPickRepo, onUpload }) => {
  const tiles: React.ReactNode[] = [];
  if (hosts?.can_connect) {
    HOSTS.forEach((h) => {
      const state = hosts[h.key];
      let action = 'Connect';
      if (state.connected) action = 'Manage or disconnect';
      tiles.push(
        <SourceTile
          key={h.key}
          icon={h.icon}
          title={h.title}
          badge={<HostBadge state={state} loading={false} />}
          line={ownerLine(state, h.title)}
          action={action}
          disabled={opening}
          onClick={onOpenCode}
        />,
      );
    });
  }
  if (!loading && !hosts?.can_connect) {
    HOSTS.filter((h) => hosts?.[h.key]?.connected).forEach((h) => {
      tiles.push(
        <SourceTile
          key={h.key}
          icon={h.icon}
          title={h.title}
          badge={<HostBadge state={hosts?.[h.key] || null} loading={false} />}
          line={memberLine(hosts?.[h.key] || null)}
          action="Pick a repository"
          disabled={opening}
          onClick={onPickRepo}
        />,
      );
    });
    if (tiles.length === 0) {
      tiles.push(
        <SourceTile
          key="link"
          icon={<Link2 size={16} />}
          title="Repository link"
          line="Paste a GitHub or GitLab link to a repository you can open."
          action="Paste a link"
          disabled={opening}
          onClick={onPickRepo}
        />,
      );
    }
  }
  tiles.push(
    <SourceTile
      key="zip"
      icon={<FileArchive size={16} />}
      title="Zip"
      line="Upload the code as a zip or a whole folder."
      action="Upload in the workspace"
      disabled={opening}
      onClick={onUpload}
    />,
  );
  return (
    <>
      <div className={clsx('grid grid-cols-1 gap-3', GRID_COLS[tiles.length] || 'sm:grid-cols-3')}>{tiles}</div>
      {loading && (
        <p className="m-0 mt-3 inline-flex items-center gap-1.5 text-[12.5px] text-ink-soft">
          <Loader2 size={12} className="animate-spin" />
          Checking GitHub and GitLab
        </p>
      )}
      <p className="m-0 mt-4 text-[13px] leading-[20px] text-ink-muted">
        Any one is enough to start. We only read it: nothing in the code changes.
      </p>
    </>
  );
};

const BuildDetail: React.FC<{ kind: KindKey; onKind: (k: KindKey) => void }> = ({ kind, onKind }) => (
  <>
    <p className="m-0 mb-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-soft">What kind</p>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" role="radiogroup" aria-label="What kind">
      {KINDS.map((k) => (
        <button
          key={k.key}
          type="button"
          role="radio"
          aria-checked={kind === k.key}
          onClick={() => onKind(k.key)}
          className={clsx(
            'rounded-act border p-4 text-left transition-colors',
            kind === k.key && 'border-ink shadow-[inset_0_0_0_1px_#18191D]',
            kind !== k.key && 'border-line hover:border-line-strong',
          )}
        >
          <span className="block text-[14px] font-semibold text-ink">{k.title}</span>
          <span className="mt-1 block text-[12.5px] leading-[19px] text-ink-muted">{k.body}</span>
        </button>
      ))}
    </div>
  </>
);

const ProblemDetail: React.FC = () => (
  <p className="m-0 text-[13.5px] leading-[22px] text-ink-muted">
    Say what is wrong in your own words. Orbit asks only the questions that change the answer, then shows the ways to
    solve it, from no build at all to a custom build, so you can pick one.
  </p>
);

const ACTION_LABEL: Record<DoorKey, string> = {
  code: 'Bring the code in',
  build: 'Start the questions',
  problem: 'Describe the problem',
};

const StartScopingPage: React.FC = () => {
  const ownId = localStorage.getItem('client_id') || '';
  const ownName = localStorage.getItem('client_name') || 'Your company';
  const providerId = localStorage.getItem('provider_id');
  const scopesForClients = Boolean(providerId) && providerId === ownId;

  const own: Target = useMemo(() => ({ id: ownId, name: ownName, initials: initialsOf(ownName), own: true }), [ownId, ownName]);
  const [target, setTarget] = useState<Target>(own);
  const [clients, setClients] = useState<PartnerClientRow[]>([]);
  const [door, setDoor] = useState<DoorKey>('code');
  const [kind, setKind] = useState<KindKey>('custom');
  const [adding, setAdding] = useState(false);
  const [hosts, setHosts] = useState<CodeHostStatus | null>(null);
  const [hostsFor, setHostsFor] = useState('');
  const { opening, open } = useOpenWorkspace();

  useEffect(() => {
    if (!target.id) return undefined;
    let live = true;
    aaiApi.getCodeHosts(target.id)
      .then((res) => {
        if (!live) return;
        setHosts(res.data);
        setHostsFor(target.id);
      })
      .catch(() => {
        if (!live) return;
        setHosts(null);
        setHostsFor(target.id);
      });
    return () => { live = false; };
  }, [target.id]);

  const loadClients = React.useCallback(() => {
    if (!scopesForClients) return Promise.resolve([] as PartnerClientRow[]);
    return aaiApi.getPartnerOverview()
      .then((res) => {
        const rows: PartnerClientRow[] = res.data?.clients || [];
        setClients(rows);
        return rows;
      })
      .catch(() => {
        setClients([]);
        return [] as PartnerClientRow[];
      });
  }, [scopesForClients]);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  const onClientAdded = (id: string) => {
    setAdding(false);
    loadClients().then((rows) => {
      const added = rows.find((c) => c.id === id);
      if (!added) return;
      setTarget({ id: added.id, name: added.name, initials: added.initials || initialsOf(added.name), own: false });
    });
  };

  const hostsLoading = hostsFor !== target.id;
  let shownHosts = hosts;
  if (hostsLoading) shownHosts = null;
  const openCodeSettings = () => {
    if (!target.id) return;
    const qs = new URLSearchParams({ workspaceId: target.id, settings: 'code' });
    open(target.id, `/customer/workspace?${qs.toString()}`);
  };
  const openUpload = () => {
    if (!target.id) return;
    const qs = new URLSearchParams({ workspaceId: target.id, start: 'zip' });
    open(target.id, `/customer/workspace?${qs.toString()}`);
  };
  const start = () => {
    if (!target.id) return;
    open(target.id, destinationFor(target.id, door, kind));
  };

  let detail: React.ReactNode = <ProblemDetail />;
  if (door === 'code') {
    detail = (
      <CodeDetail
        hosts={shownHosts}
        loading={hostsLoading}
        opening={opening}
        onOpenCode={openCodeSettings}
        onPickRepo={start}
        onUpload={openUpload}
      />
    );
  }
  if (door === 'build') detail = <BuildDetail kind={kind} onKind={setKind} />;

  let actionIcon: React.ReactNode = <ArrowUpRight size={15} />;
  if (opening) actionIcon = <Loader2 size={15} className="animate-spin" />;

  let blocked = '';
  if (!target.id) blocked = 'Your company is not loaded yet. Sign in again.';
  if (opening) blocked = 'Opening the workspace';

  let whereItOpens = `Opens ${target.name}'s workspace in a new tab.`;
  if (target.own) whereItOpens = 'Opens your workspace in a new tab.';

  return (
    <PageBody>
      <PageHead
        title="What are you starting with?"
        align="center"
        actions={scopesForClients && (
          <ForPicker own={own} clients={clients} value={target} onChange={setTarget} onAddClient={() => setAdding(true)} />
        )}
      />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {DOORS.map((d) => (
          <DoorCard key={d.key} door={d} on={door === d.key} onPick={() => setDoor(d.key)} />
        ))}
      </div>
      <Panel pad="lg" className="mt-5">
        {detail}
        <div className="mt-5 flex flex-col items-center gap-2 border-t border-line-soft pt-5 text-center">
          <Action size="lg" icon={actionIcon} onClick={start} disabled={!!blocked} reason={blocked}>
            {ACTION_LABEL[door]}
          </Action>
          <span className="text-[12.5px] text-ink-soft">{whereItOpens}</span>
        </div>
      </Panel>
      {adding && <AddClientModal onClose={() => setAdding(false)} onAdded={onClientAdded} />}
    </PageBody>
  );
};

export default StartScopingPage;
